import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { UserPlus, Clock, CheckCircle, Users } from 'lucide-react';
import {
  TournamentConfig,
  signupSchema,
  teamSignupSchema,
  addSignup,
  isSignupOpen,
  getSignupOpensAt,
  getSignups,
} from '@/services/tournamentService';

interface SignupFormProps {
  tournament: TournamentConfig;
  onSignupComplete?: () => void;
}

export function SignupForm({ tournament, onSignupComplete }: SignupFormProps) {
  const { language } = useLanguage();
  const isTeam = tournament.tournamentFormat === 'team_americano';
  const schema = isTeam ? teamSignupSchema : signupSchema;
  type FormValues = z.infer<typeof teamSignupSchema>;

  const [isOpen, setIsOpen] = useState(false);
  const [countdown, setCountdown] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [waitlistPosition, setWaitlistPosition] = useState<number | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema) as any,
    defaultValues: { name: '', email: '', phone: '', partnerName: '', partnerEmail: '', partnerPhone: '' },
  });

  useEffect(() => {
    const check = () => {
      if (isSignupOpen(tournament)) {
        setIsOpen(true);
        return;
      }

      const opensAt = getSignupOpensAt(tournament);
      const now = new Date();
      const diff = opensAt.getTime() - now.getTime();

      if (diff <= 0) {
        setIsOpen(true);
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      setCountdown(
        language === 'no'
          ? `${days} dager, ${hours} timer, ${minutes} minutter`
          : `${days} days, ${hours} hours, ${minutes} minutes`
      );
    };

    check();
    const interval = setInterval(check, 60000);
    return () => clearInterval(interval);
  }, [tournament, language]);

  const onSubmit = async (values: FormValues) => {
    setError('');
    setSubmitting(true);
    try {
      const hasPartner = isTeam && values.partnerName && values.partnerEmail && values.partnerPhone;
      const partner = hasPartner
        ? { name: values.partnerName!, email: values.partnerEmail!, phone: values.partnerPhone! }
        : undefined;

      const result = await addSignup({
        name: values.name,
        email: values.email,
        phone: values.phone,
        tournamentId: tournament.id,
        ...(partner ? { partner } : {}),
      });

      if (result.success) {
        setSubmitted(true);
        const signups = await getSignups(tournament.id);
        const position = signups.length;
        if (position > tournament.maxPlayers) {
          setWaitlistPosition(position - tournament.maxPlayers);
        }
        onSignupComplete?.();
      } else {
        setError(result.message);
      }
    } catch (e: any) {
      setError(e.message || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-accent/50 p-6 rounded-lg text-center space-y-2">
        <CheckCircle className="h-8 w-8 text-primary mx-auto" />
        <p className="font-semibold text-foreground">
          {isTeam
            ? (language === 'no' ? 'Lagpåmelding bekreftet!' : 'Team signup confirmed!')
            : (language === 'no' ? 'Påmelding bekreftet!' : 'Signup confirmed!')}
        </p>
        {waitlistPosition && (
          <p className="text-sm text-muted-foreground">
            {language === 'no'
              ? `Du er nummer ${waitlistPosition} på ventelisten.`
              : `You are number ${waitlistPosition} on the waitlist.`}
          </p>
        )}
      </div>
    );
  }

  if (!isOpen) {
    const opensAt = getSignupOpensAt(tournament);
    const formattedDate = opensAt.toLocaleDateString(language === 'no' ? 'nb-NO' : 'en-US', {
      weekday: 'long',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
    const formattedTime = opensAt.toLocaleTimeString(language === 'no' ? 'nb-NO' : 'en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    return (
      <div className="bg-accent/50 p-6 rounded-lg space-y-2">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Clock className="h-5 w-5" />
          <span className="font-medium">
            {language === 'no' ? 'Påmelding åpner om' : 'Signup opens in'}
          </span>
        </div>
        <p className="text-lg font-bold text-primary">{countdown}</p>
        <p className="text-sm text-muted-foreground">
          {language === 'no'
            ? `${formattedDate}, kl. ${formattedTime}`
            : `${formattedDate}, ${formattedTime}`}
        </p>
        <p className="text-xs text-muted-foreground">
          {language === 'no' ? '(72 timer før turneringen starter)' : '(72 hours before the tournament starts)'}
        </p>
      </div>
    );
  }

  const PlayerFields = ({ prefix, label }: { prefix: '' | 'partner'; label: string }) => {
    const nameField = prefix ? 'partnerName' : 'name';
    const emailField = prefix ? 'partnerEmail' : 'email';
    const phoneField = prefix ? 'partnerPhone' : 'phone';

    return (
      <div className="space-y-3">
        {isTeam && (
          <p className="text-sm font-medium text-muted-foreground">{label}</p>
        )}
        <FormField
          control={form.control}
          name={nameField as any}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{language === 'no' ? 'Navn' : 'Name'}</FormLabel>
              <FormControl>
                <Input placeholder={language === 'no' ? 'Ola Nordmann' : 'John Doe'} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={emailField as any}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{language === 'no' ? 'E-post' : 'Email'}</FormLabel>
              <FormControl>
                <Input type="email" placeholder="ola@example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={phoneField as any}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{language === 'no' ? 'Telefon' : 'Phone'}</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+47 123 45 678" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <h3 className="font-semibold text-lg flex items-center gap-2 text-foreground">
        {isTeam ? <Users className="h-5 w-5 text-primary" /> : <UserPlus className="h-5 w-5 text-primary" />}
        {isTeam
          ? (language === 'no' ? 'Meld på laget ditt' : 'Sign up your team')
          : (language === 'no' ? 'Meld deg på' : 'Sign up')}
      </h3>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <PlayerFields prefix="" label={language === 'no' ? 'Spiller 1' : 'Player 1'} />

          {isTeam && (
            <>
              <div className="border-t border-border my-2" />
              <PlayerFields prefix="partner" label={language === 'no' ? 'Spiller 2 - Partner (valgfritt)' : 'Player 2 - Partner (optional)'} />
            </>
          )}

          {error && <p className="text-sm text-destructive font-medium">{error}</p>}

          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting
              ? '...'
              : isTeam
                ? (language === 'no' ? 'Meld på laget' : 'Sign up team')
                : (language === 'no' ? 'Meld deg på' : 'Sign up')}
          </Button>
        </form>
      </Form>
    </div>
  );
}
